import createClient from 'openapi-fetch';
import type { paths } from './schema';
import { createCsrfMiddlewareBundle } from './csrf';
import { createForbiddenMiddleware } from './forbidden';
import { createStepUpMiddleware, type StepUpQueueLike } from './step-up';
import { createUnauthMiddleware } from './unauth';

export interface CreateApiClientOptions {
  /**
   * Step-up queue (typically the singleton from `@sentinel/auth-core`).
   * When provided, a 403 `step_up_required` response is parked on the queue,
   * a `sentinel:step-up-required` window event is dispatched (so a
   * StepUpProvider can show the dialog), and the original request is retried
   * once the queue entry resolves.
   */
  stepUpQueue?: StepUpQueueLike;
  /** See StepUpMiddlewareOptions.isFederatedUser. */
  isFederatedUser?: () => boolean;
}

export function createApiClient(options: CreateApiClientOptions = {}) {
  const client = createClient<paths>({ baseUrl: '/', credentials: 'same-origin' });
  const csrf = createCsrfMiddlewareBundle();
  // Registered first on purpose. openapi-fetch walks `onResponse` in reverse
  // registration order, so the first middleware registered is the last one to
  // see a response — which is exactly what this one needs: it must judge the
  // 403 only after step-up has replayed the request and after CSRF has spent
  // its one-shot retry. Register it any later and it announces a permission
  // error for a 403 a sibling middleware is about to resolve.
  client.use(createForbiddenMiddleware());
  client.use(csrf.middleware);
  if (options.stepUpQueue) {
    // The step-up retry must re-apply CSRF against the freshly rotated
    // session/cookie. openapi-fetch has no public hook to re-run the
    // middleware chain on a raw Request, so we replicate the relevant logic
    // (CSRF re-application sharing the same meta cache) here.
    client.use(
      createStepUpMiddleware({
        queue: options.stepUpQueue,
        ...(options.isFederatedUser ? { isFederatedUser: options.isFederatedUser } : {}),
        replay: async (request) => {
          await csrf.applyToRequest(request);
          return fetch(request);
        },
      }),
    );
  }
  // Last in chain: a 401 reaching this point has already been through any
  // step-up retry (which would have either consumed the 403 or returned a
  // different status). A 401 here represents a true session-invalid signal.
  client.use(createUnauthMiddleware());
  return client;
}

export type ApiClient = ReturnType<typeof createApiClient>;
